
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useMigrationStore } from '@/store/migrationStore';
import { getUserPinOrThrow, shouldUsePinMode } from '@/utils/pinModeUtils';

interface ManagerStats {
  activeEmployees: number;
  totalHoursThisWeek: number;
  pendingTimesheets: number;
}

export const useManagerStats = () => {
  const [stats, setStats] = useState<ManagerStats>({
    activeEmployees: 0,
    totalHoursThisWeek: 0,
    pendingTimesheets: 0,
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { isActive, source } = useMigrationStore();

  useEffect(() => {
    fetchManagerStats();
  }, [isActive, source]);

  const fetchManagerStats = async () => {
    try {
      setLoading(true);

      // Déterminer quelle table utiliser selon le mode migration
      const timeEntriesTable = isActive && source === 'staging' ? 'time_entries_staging' : 'time_entries';
      const usePinMode = shouldUsePinMode();
      
      // Calculer les dates de début et fin de cette semaine
      const now = new Date();
      const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay() + 1)); // Lundi
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      const computeMinutes = (entry: { duration_minutes: number | null; start_time: string; end_time: string | null }) => {
        if (typeof entry.duration_minutes === 'number' && !Number.isNaN(entry.duration_minutes)) {
          return entry.duration_minutes;
        }
        if (entry.end_time) {
          const diff = (new Date(entry.end_time).getTime() - new Date(entry.start_time).getTime()) / 60000;
          return Math.max(0, Math.round(diff));
        }
        return 0;
      };

      const fetchPinEntriesPaged = async () => {
        const pin = getUserPinOrThrow();
        const all: any[] = [];
        const pageSize = 1000;
        let offset = 0;
        const maxPages = 20;
        for (let page = 0; page < maxPages; page++) {
          const { data, error } = await supabase.rpc('pin_list_time_entries_range', {
            p_pin: pin,
            p_start: startOfWeek.toISOString(),
            p_end: endOfWeek.toISOString(),
            p_limit: pageSize,
            p_offset: offset,
            p_order: 'asc',
          });
          if (error) throw error;
          const rows = (data ?? []) as any[];
          all.push(...rows);
          if (rows.length < pageSize) break;
          offset += pageSize;
        }
        return all;
      };

      const { data: timeEntries, error: timeEntriesError } = usePinMode
        ? { data: await fetchPinEntriesPaged(), error: null as any }
        : await supabase
            .from(timeEntriesTable)
            .select('user_id, start_time, end_time, duration_minutes')
            .gte('start_time', startOfWeek.toISOString())
            .lte('start_time', endOfWeek.toISOString());

      if (timeEntriesError) throw timeEntriesError;

      const entries = (timeEntries ?? []) as Array<{
        user_id: string;
        start_time: string;
        end_time: string | null;
        duration_minutes: number | null;
      }>;

      const totalMinutes = entries.reduce((sum, entry) => sum + computeMinutes(entry), 0);
      const totalHours = Math.round((totalMinutes / 60) * 100) / 100;
      const activeEmployeesCount = new Set(entries.map((entry) => entry.user_id)).size;
      const pendingCount = entries.filter((entry) => !entry.end_time).length;

      setStats({
        activeEmployees: activeEmployeesCount,
        totalHoursThisWeek: totalHours,
        pendingTimesheets: pendingCount,
      });

    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les statistiques",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return { stats, loading, refetch: fetchManagerStats };
};
